import type { ApprovalResolution } from './approval-resolution-text';

/**
 * Banner palette for an entry needing attention, keyed on its status.
 *
 * Red is a person's judgement (`rejected`); amber is the approval system
 * failing to process the entry (`failed` — no policy matched, or no
 * approver could be routed). Since a no-match auto-rejection now lands in
 * `failed` rather than `rejected`, that colour split is the glanceable
 * half of "did a human decide this?", and it only works if every surface
 * agrees. The read-only locked view used to hardcode red, so the same
 * entry read red there and amber in the editor.
 */
export function attentionBannerClass(status: 'rejected' | 'failed' | string): string {
  return status === 'rejected'
    ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-800/60 dark:bg-red-900/20 dark:text-red-200'
    : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/60 dark:bg-amber-900/20 dark:text-amber-200';
}

/**
 * The `resolution_reason` body of a rejection banner, rendered in the voice
 * of whoever wrote it.
 *
 * An approver's note gets a caption and a quoted, italic, rule-marked block;
 * the app's own copy gets none of that and reads as plain prose. Without the
 * split, the two sat side by side in identical type and readers could not
 * tell which sentence a person had written — the reason they opened the row
 * at all — from boilerplate.
 *
 * Colour comes from the surrounding banner (rejected is red, failed amber),
 * so everything here is `current`-relative and works under either.
 */
export function ApprovalResolutionNote({ resolution }: { resolution: ApprovalResolution }) {
  if (resolution.kind === 'system') {
    return <p className="mt-1 leading-snug first:mt-0">{resolution.text}</p>;
  }
  return (
    <figure className="mt-1.5 border-l-2 border-current/30 pl-2 first:mt-0">
      <figcaption className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
        Approver's note
      </figcaption>
      <blockquote className="mt-0.5 italic leading-snug">“{resolution.text}”</blockquote>
    </figure>
  );
}
